import { useState, useEffect } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { api, post, del } from '../lib/api.js';

type Tab = 'general' | 'team' | 'notifications' | 'integrations' | 'monitoring';

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('general');

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'general', label: 'General' },
    { key: 'team', label: 'Users & Roles' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'integrations', label: 'Integrations' },
    { key: 'monitoring', label: 'Monitoring' },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Settings</h2>
      <p className="text-gray-600 mb-6">Configure The One Protect for your organization.</p>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'general' && <GeneralSettings />}
      {activeTab === 'team' && <TeamSettings />}
      {activeTab === 'notifications' && <NotificationSettings />}
      {activeTab === 'integrations' && <IntegrationSettings />}
      {activeTab === 'monitoring' && <MonitoringSettings />}
    </div>
  );
}

// ── General Settings ──
function GeneralSettings() {
  const { data, loading } = useApi<{ data: { org_name?: string; primary_contact?: string; timezone?: string; data_retention_days?: number } }>('/protect/settings');
  const [orgName, setOrgName] = useState('');
  const [contact, setContact] = useState('');
  const [timezone, setTimezone] = useState('America/Chicago');
  const [retention, setRetention] = useState(365);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.data) {
      setOrgName(data.data.org_name ?? '');
      setContact(data.data.primary_contact ?? '');
      setTimezone(data.data.timezone ?? 'America/Chicago');
      setRetention(data.data.data_retention_days ?? 365);
    }
  }, [data]);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    try {
      await api('/protect/settings', {
        method: 'PATCH',
        body: JSON.stringify({ org_name: orgName, primary_contact: contact, timezone, data_retention_days: retention }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-gray-500">Loading settings...</div>;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">General Settings</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
          <input type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Primary Contact Email</label>
          <input type="email" value={contact} onChange={(e) => setContact(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
            <option value="America/New_York">Eastern</option>
            <option value="America/Chicago">Central</option>
            <option value="America/Denver">Mountain</option>
            <option value="America/Los_Angeles">Pacific</option>
            <option value="UTC">UTC</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data Retention (days)</label>
          <input type="number" value={retention} onChange={(e) => setRetention(parseInt(e.target.value) || 365)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" min={30} max={730} />
        </div>
        <div className="flex items-center gap-3 pt-2">
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {saved && <span className="text-sm text-emerald-600">Saved!</span>}
        </div>
      </div>
    </div>
  );
}

// ── Team Settings ──
function TeamSettings() {
  const { data, loading, refresh } = useApi<{ data: Array<{ id: string; email: string; name?: string; role: string; invited_at: string }> }>('/protect/settings/team');
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('viewer');
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const members = data?.data ?? [];

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await post('/protect/settings/team/invite', { email, name: name || undefined, role });
      setShowInvite(false);
      setEmail('');
      setName('');
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to invite');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(userId: string) {
    if (!confirm('Remove this team member?')) return;
    setActionLoading(userId);
    try {
      await del(`/protect/settings/team/${userId}`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRoleChange(userId: string, newRole: string) {
    setActionLoading(userId);
    try {
      await api(`/protect/settings/team/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole }),
      });
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  }

  if (loading) return <div className="text-gray-500">Loading team...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Team Members</h3>
        <button onClick={() => setShowInvite(true)} className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700">
          + Invite User
        </button>
      </div>

      {showInvite && (
        <form onSubmit={handleInvite} className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button type="submit" disabled={submitting} className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50">
                {submitting ? 'Inviting...' : 'Invite'}
              </button>
              <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200">Cancel</button>
            </div>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Invited</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No team members yet. Invite users to get started.</td></tr>
            ) : members.map((m) => (
              <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{m.name ?? '—'}</td>
                <td className="px-4 py-3 text-gray-600">{m.email}</td>
                <td className="px-4 py-3">
                  <select
                    value={m.role}
                    onChange={(e) => handleRoleChange(m.id, e.target.value)}
                    disabled={actionLoading === m.id}
                    className="border border-gray-300 rounded-md px-2 py-1 text-xs"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-gray-500">{new Date(m.invited_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => handleRemove(m.id)} disabled={actionLoading === m.id} className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50">Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Notification Settings ──
function NotificationSettings() {
  const { data, loading } = useApi<{ data: { rules: Array<{ event: string; email: boolean; in_app: boolean; slack: boolean }>; digest_frequency: string } }>('/protect/settings/notifications');
  const [rules, setRules] = useState<Array<{ event: string; email: boolean; in_app: boolean; slack: boolean }>>([]);
  const [digest, setDigest] = useState('daily');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.data) {
      setRules(data.data.rules);
      setDigest(data.data.digest_frequency);
    }
  }, [data]);

  function toggleRule(idx: number, channel: 'email' | 'in_app' | 'slack') {
    setRules(prev => prev.map((r, i) => i === idx ? { ...r, [channel]: !r[channel] } : r));
  }

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    try {
      await api('/protect/settings/notifications', {
        method: 'PATCH',
        body: JSON.stringify({ rules, digest_frequency: digest }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const eventLabels: Record<string, string> = {
    dark_web_alert: 'Dark Web Alert',
    phishing_fail: 'Phishing Test Failed',
    saas_risk: 'SaaS Risk Event',
    dmarc_fail: 'DMARC Failure',
    training_due: 'Training Due',
    email_alert: 'Email Security Alert',
    shadow_ai_detection: 'Shadow AI Detection',
    blacklist_detected: 'Blacklist Detection',
  };

  if (loading) return <div className="text-gray-500">Loading notification settings...</div>;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-3xl">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Notification Preferences</h3>

      <table className="w-full text-sm mb-6">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 font-medium text-gray-600">Event</th>
            <th className="text-center py-2 font-medium text-gray-600 w-20">Email</th>
            <th className="text-center py-2 font-medium text-gray-600 w-20">In-App</th>
            <th className="text-center py-2 font-medium text-gray-600 w-20">Slack</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((r, idx) => (
            <tr key={r.event} className="border-b border-gray-100">
              <td className="py-2 text-gray-900">{eventLabels[r.event] ?? r.event}</td>
              <td className="py-2 text-center">
                <input type="checkbox" checked={r.email} onChange={() => toggleRule(idx, 'email')} className="rounded" />
              </td>
              <td className="py-2 text-center">
                <input type="checkbox" checked={r.in_app} onChange={() => toggleRule(idx, 'in_app')} className="rounded" />
              </td>
              <td className="py-2 text-center">
                <input type="checkbox" checked={r.slack} onChange={() => toggleRule(idx, 'slack')} className="rounded" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Digest Frequency</label>
        <select value={digest} onChange={(e) => setDigest(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 text-sm">
          <option value="realtime">Real-time</option>
          <option value="hourly">Hourly</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        {saved && <span className="text-sm text-emerald-600">Saved!</span>}
      </div>
    </div>
  );
}

// ── Integration Settings ──
function IntegrationSettings() {
  const { data, loading, refresh } = useApi<{ data: Array<{ id: string; product: string; product_label: string; enabled: boolean; status: string; last_sync_at?: string }> }>('/protect/settings/integrations');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const integrations = data?.data ?? [];

  async function handleConnect(product: string) {
    setActionLoading(product);
    try {
      await post(`/protect/settings/integrations/${product}/connect`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to connect');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleTest(product: string) {
    setActionLoading(`test-${product}`);
    try {
      await post(`/protect/settings/integrations/${product}/test`);
      toast.success('Connection test successful');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDisconnect(product: string) {
    if (!confirm('Disconnect this integration?')) return;
    setActionLoading(product);
    try {
      await post(`/protect/settings/integrations/${product}/disconnect`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect');
    } finally {
      setActionLoading(null);
    }
  }

  if (loading) return <div className="text-gray-500">Loading integrations...</div>;

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Integrations</h3>
      <div className="grid grid-cols-2 gap-4">
        {integrations.map((int) => (
          <div key={int.id} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-gray-900">{int.product_label}</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                int.status === 'connected' ? 'bg-emerald-100 text-emerald-700' :
                int.status === 'error' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-500'
              }`}>{int.status === 'connected' ? 'Connected' : int.status === 'error' ? 'Error' : 'Not Connected'}</span>
            </div>
            {int.last_sync_at && (
              <p className="text-xs text-gray-500 mb-3">Last sync: {new Date(int.last_sync_at).toLocaleString()}</p>
            )}
            <div className="flex gap-2">
              {int.status === 'connected' ? (
                <>
                  <button
                    onClick={() => handleTest(int.product)}
                    disabled={actionLoading === `test-${int.product}`}
                    className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    Test
                  </button>
                  <button
                    onClick={() => handleDisconnect(int.product)}
                    disabled={actionLoading === int.product}
                    className="px-3 py-1 bg-red-50 text-red-600 text-xs font-medium rounded-md hover:bg-red-100 disabled:opacity-50"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect(int.product)}
                  disabled={actionLoading === int.product}
                  className="px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  Connect
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Monitoring Settings ──
function MonitoringSettings() {
  const { data, loading } = useApi<{
    data: {
      dark_web: { domains: string[]; alert_frequency: string };
      shadow_ai: { detection_sensitivity: string; approved_tools: string[] };
      dmarc: { monitoring_frequency: string; report_aggregation: string };
      email: { alert_thresholds: { critical: number; high: number }; auto_quarantine: boolean };
    }
  }>('/protect/settings/monitoring');
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.data) {
      setConfig(data.data as unknown as Record<string, unknown>);
    }
  }, [data]);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    try {
      await api('/protect/settings/monitoring', {
        method: 'PATCH',
        body: JSON.stringify(config),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-gray-500">Loading monitoring config...</div>;

  const darkWeb = (config.dark_web ?? {}) as { alert_frequency?: string };
  const shadowAi = (config.shadow_ai ?? {}) as { detection_sensitivity?: string };
  const dmarc = (config.dmarc ?? {}) as { monitoring_frequency?: string; report_aggregation?: string };
  const emailConfig = (config.email ?? {}) as { auto_quarantine?: boolean };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Dark Web Monitoring</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Alert Frequency</label>
          <select
            value={darkWeb.alert_frequency ?? 'daily'}
            onChange={(e) => setConfig(prev => ({ ...prev, dark_web: { ...darkWeb, alert_frequency: e.target.value } }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="realtime">Real-time</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Shadow AI Detection</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Detection Sensitivity</label>
          <select
            value={shadowAi.detection_sensitivity ?? 'medium'}
            onChange={(e) => setConfig(prev => ({ ...prev, shadow_ai: { ...shadowAi, detection_sensitivity: e.target.value } }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="low">Low (fewer alerts)</option>
            <option value="medium">Medium</option>
            <option value="high">High (more alerts)</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">DMARC Monitoring</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monitoring Frequency</label>
            <select
              value={dmarc.monitoring_frequency ?? 'every_4h'}
              onChange={(e) => setConfig(prev => ({ ...prev, dmarc: { ...dmarc, monitoring_frequency: e.target.value } }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="every_4h">Every 4 hours</option>
              <option value="every_12h">Every 12 hours</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Aggregation Period</label>
            <select
              value={dmarc.report_aggregation ?? '30d'}
              onChange={(e) => setConfig(prev => ({ ...prev, dmarc: { ...dmarc, report_aggregation: e.target.value } }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="7d">7 days</option>
              <option value="30d">30 days</option>
              <option value="90d">90 days</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Email Security</h3>
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={emailConfig.auto_quarantine ?? false}
            onChange={(e) => setConfig(prev => ({ ...prev, email: { ...emailConfig, auto_quarantine: e.target.checked } }))}
            className="rounded"
            id="auto-quarantine"
          />
          <label htmlFor="auto-quarantine" className="text-sm text-gray-700">Enable auto-quarantine for suspicious emails</label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
        {saved && <span className="text-sm text-emerald-600">Saved!</span>}
      </div>
    </div>
  );
}
